import React, { useState, useEffect } from "react";

import {
  FiMoreVertical,
  FiEdit2,
  FiTrash,
  FiPlus,
  FiShare2,
  FiX,
  FiChevronRight,
  FiChevronLeft,
  FiCopy,
  FiMenu,
  FiSettings,
  FiGrid,
  FiTag,
  FiZap,
  FiUsers,
} from "react-icons/fi";

import Input from "../../../../../../app/components/Input";
import DropDown from "../../../../../../app/components/Dropdown";
import  Select  from "../../../../../../app/components/Select";
import { Button } from "../../../../../../app/components/Button";
import Accordion from "../../../../../../app/components/Accordion";

import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
} from "framer-motion";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

export default function OffCanvasSettings({ isOpen, onClose }) {
  const [formularioSelecionado, setFormularioSelecionado] = useState("formulario-inicial");
  const [painelExpandido, setPainelExpandido] = useState(false);

  const posicaoPainel = useMotionValue(400);
  const posicaoAnimada = useSpring(posicaoPainel, { stiffness: 300, damping: 30 });

  const sensoresDeArraste = useSensors(useSensor(PointerSensor));

  const camposIniciais = [
    { id: "nomeCliente", label: "Nome do Cliente", required: true },
    { id: "email", label: "Email", type: "email" },
    { id: "telefone", label: "Telefone" },
    { id: "cnpj", label: "CNPJ" },
    { id: "razaoSocial", label: "Razão Social" },
    { id: "nomeFantasia", label: "Nome Fantasia" },
  ];

  const [camposOrdenados, setCamposOrdenados] = useState(camposIniciais);

  const opcoesDeFormularios = [
    { value: "formulario-inicial", label: "Formulário Inicial" },
    { value: "Lead Quente", label: "Formulário Avançado" },
    { value: "Negociação", label: "Negociação" },
    { value: "Abertura Vendida", label: "Abertura Vendida" },
    { value: "Ganho (Falta Contrato)", label: "Ganho (Falta Contrato)" },
    { value: "Contrato Assinado", label: "Contrato Assinado" },
    { value: "Perdido", label: "Perdido" },
    { value: "Não Convertido", label: "Não Convertido" },
    { value: "Arquivo", label: "Arquivo" },
  ];

  const [secoesAccordion, setSecoesAccordion] = useState([
    {
      id: "configuracao",
      title: "Configuração da Sequência",
      icon: <FiSettings className="text-purple-700" />,
      expanded: false,
      content: (
        <div className="p-4 text-sm text-gray-600">
          Aqui você pode ajustar a sequência inicial, tempo, etc.
        </div>
      ),
    },
    {
      id: "condicionais",
      title: "Campos Condicionais",
      icon: <FiGrid className="text-yellow-600" />,
      expanded: false,
      content: (
        <div className="p-4 text-sm text-gray-600">
          Defina condições para exibir ou ocultar campos.
        </div>
      ),
    },
    {
      id: "etiquetas",
      title: "Etiquetas",
      icon: <FiTag className="text-purple-700" />,
      count: 0,
      expanded: false,
      content: (
        <div className="p-4 text-sm text-gray-600">
          Gerencie etiquetas e categorias para esse fluxo.
        </div>
      ),
    },
    {
      id: "automacao",
      title: "Automação",
      icon: <FiZap className="text-green-700" />,
      count: 92,
      expanded: false,
      content: (
        <div className="p-4 text-sm text-gray-600">
          Configure gatilhos e ações automáticas.
        </div>
      ),
    },
    {
      id: "colaboradores",
      title: "Colaboradores (Acessos e Permissões)",
      icon: <FiUsers className="text-purple-700" />,
      count: 16,
      expanded: false,
      content: (
        <div className="p-4 text-sm text-gray-600">
          Adicione ou remova membros com diferentes permissões.
        </div>
      ),
    },
  ]);

  useEffect(() => {
    if (isOpen) {
      setPainelExpandido(false);
      posicaoPainel.set(400);
    }
  }, [isOpen, posicaoPainel]);

  function alternarPainel() {
    const novoEstado = !painelExpandido;
    setPainelExpandido(novoEstado);
    posicaoPainel.set(novoEstado ? 0 : 400);
  }

  function fecharPainel() {
    setPainelExpandido(false);
    posicaoPainel.set(500);
    setTimeout(() => onClose(), 250);
  }

  function onToggleSection(id) {
    setSecoesAccordion((anteriores) =>
      anteriores.map((secao) =>
        secao.id === id ? { ...secao, expanded: !secao.expanded } : secao
      )
    );
  }

  function ItemOrdenavel({ id, children }) {
    const {
      setNodeRef,
      transform,
      transition,
      attributes,
      listeners,
      isDragging,
    } = useSortable({ id });

    const estiloTransformado = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const estiloBorda = isDragging
      ? "border-[#3f1057]/60 border-dashed bg-gray-50"
      : "border-transparent";

    return (
      <div
        ref={setNodeRef}
        style={estiloTransformado}
        {...attributes}
        className={`relative border-2 rounded-xl ${estiloBorda}`}
      >
        {children({ listeners })}
      </div>
    );
  }

  function renderizarCampo(campo) {
    return (
      <ItemOrdenavel key={campo.id} id={campo.id}>
        {({ listeners }) => (
          <div className="flex gap-4 items-start">
            <div className="mt-3 text-gray-400 cursor-move" {...listeners}>
              <FiMenu size={18} />
            </div>
            <div className="flex-1">
              {campo.isSelect ? (
                <Select
                  id={campo.id}
                  label={campo.label}
                  required={campo.required}
                  options={[]}
                  disabled
                  className="opacity-50 pointer-events-none select-none"
                />
              ) : (
                <Input
                  id={campo.id}
                  label={campo.label}
                  type={campo.type || "text"}
                  required={campo.required}
                  disabled
                  className="opacity-50 pointer-events-none select-none mb-[-10px]"
                  size="xs"
                />
              )}
            </div>
            <div className="relative mt-3">
              <DropDown
                trigger={
                  <button type="button" className="focus:outline-none">
                    <FiMoreVertical size={18} className="text-gray-400 hover:text-gray-600" />
                  </button>
                }
                options={[
                  { label: "Editar", icon: FiEdit2, onClick: () => console.log("Editar:", campo.id) },
                  { label: "Duplicar", icon: FiCopy, onClick: () => console.log("Duplicar:", campo.id) },
                  { label: "Apagar", icon: FiTrash, onClick: () => console.log("Apagar:", campo.id) },
                ]}
              />
            </div>
          </div>
        )}
      </ItemOrdenavel>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50">
          <motion.div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={fecharPainel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="absolute top-0 right-0 h-full z-50"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4 }}
          >
            <motion.div
              className="relative h-full flex overflow-x-hidden"
              style={{ width: 1050, x: posicaoAnimada }}
              drag="x"
              dragConstraints={{ left: -10, right: 400 }}
              dragElastic={0.05}
              onDragEnd={(evento, informacoes) => {
                const deslocamento = informacoes.offset.x;
                if (deslocamento > 100 && !painelExpandido) {
                  setPainelExpandido(true);
                  posicaoPainel.set(0);
                } else {
                  posicaoPainel.set(painelExpandido ? 0 : 400);
                }
              }}
            >
              {/* Painel Esquerdo - Formulários */}
              <div className="relative bg-white shadow-2xl rounded-2xl overflow-hidden h-[calc(100%-4rem)] my-8 mr-3 w-[550px]">
                <div className="flex items-center justify-end px-6 pt-3">
                  <button onClick={fecharPainel} className="text-gray-500 hover:text-gray-700 p-1">
                    <FiX size={22} />
                  </button>
                </div>

                <div className="px-6">
                  <h2 className="text-lg font-semibold text-gray-800">Formulários</h2>
                </div>

                <div className="p-5 space-y-6 overflow-y-auto max-h-[calc(100%-5rem)] pr-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Select
                      id="formulario"
                      name="formulario"
                      size="sm"
                      label=" "
                      value={formularioSelecionado}
                      onChange={(evento) => setFormularioSelecionado(evento.target.value)}
                      options={opcoesDeFormularios}
                      className="w-[220px] h-10 flex-1"
                    />
                    <span
                      onClick={() => console.log("Compartilhar formulário")}
                      className="h-10 px-4 text-sm flex items-center gap-2 text-[#3f1057] font-semibold cursor-pointer"
                    >
                      <FiShare2 size={16} /> Compartilhar Formulário
                    </span>
                    <Button
                      onClick={() => console.log("Adicionar novo campo")}
                      variant="primary"
                      icon={<FiPlus size={16} />}
                      className="h-10 w-10 p-0 flex items-center justify-center"
                    />
                  </div>

                  <p className="text-sm text-gray-500">
                    Filtre um formulário, edite ou apague um campo utilizando as opções.
                  </p>

                  <DndContext
                    sensors={sensoresDeArraste}
                    collisionDetection={closestCenter}
                    onDragEnd={({ active, over }) => {
                      if (active.id !== over?.id) {
                        const indiceAtual = camposOrdenados.findIndex(c => c.id === active.id);
                        const novoIndice = camposOrdenados.findIndex(c => c.id === over.id);
                        setCamposOrdenados((anteriores) => arrayMove(anteriores, indiceAtual, novoIndice));
                      }
                    }}
                  >
                    <SortableContext
                      items={camposOrdenados.map(campo => campo.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="flex flex-col gap-4">
                        {camposOrdenados.map(renderizarCampo)}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>

              {/* Painel Direito - Configurações */}
              <div className="relative bg-white shadow-xl rounded-2xl overflow-hidden h-[calc(100%-4rem)] my-8 w-[500px]">
                <div className="absolute top-3 left-3">
                  <button onClick={alternarPainel} className="p-1 text-gray-600 hover:text-gray-800">
                    {painelExpandido ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
                  </button>
                </div>

                <div className="flex items-center justify-end px-4 pt-3">
                  <button onClick={fecharPainel} className="text-gray-500 hover:text-gray-700 p-1">
                    <FiX size={20} />
                  </button>
                </div>

                <div className="px-6">
                  <h2 className="text-lg font-semibold text-gray-800">Configurações</h2>
                </div>

                <div className="px-6 py-4">
                  <Accordion
                    sections={secoesAccordion}
                    onToggleSection={onToggleSection}
                    emptyStateMessage="Nenhum item configurado"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
